import React from 'react';
import pt from 'prop-types';
import { connect } from 'react-redux';
import Icon from 'components/icon';
import ViewLoading from 'components/view/loading';
import ImageLoader from 'components/loading/image';
import Loading from 'components/loading';
import StorefrontsPage from 'components/storefront';
import { classNames } from 'utils';
import actions from 'actions';

export const mapStateToProps = (state) => ({
  storefronts: state.storefronts.all,
  record: state.storefronts.storefront,
  loading: state.storefronts.loading,
  installedApps: state.client.apps,
  deprecatedApps: state.apps.deprecatedApps,
  storefrontUpdates: state.apps.storefrontUpdates,
});

export const mapDispatchToProps = (dispatch) => ({
  fetchAllStorefronts: () => {
    return dispatch(actions.storefronts.fetchAll());
  },

  fetchAllDeprecatedApps: () => {
    return dispatch(actions.apps.fetchAllDeprecatedApps());
  },

  fetchStorefront: (id) => {
    return dispatch(actions.storefronts.fetch(id));
  },

  updateStorefront: (id, data) => {
    return dispatch(actions.storefronts.update(id, data));
  },

  createStorefront: (data) => {
    return dispatch(actions.storefronts.create(data));
  },

  deleteStorefront: (id, data) => {
    return dispatch(actions.storefronts.delete(id, data));
  },

  updateApp: async (id, data) => {
    const result = await dispatch(actions.client.updateApp(id, data));
    // Reset content models after updating
    await dispatch(actions.content.fetchModels());
    return result;
  },

  fetchUpgradeConflicts: (storefront, themeVersion) => {
    return dispatch(
      actions.storefronts.fetchUpgradeConflicts(storefront, themeVersion),
    );
  },

  autoUpdateInstalledThemes: () => {
    return dispatch(actions.apps.autoUpdateInstalledThemes());
  },

  fetchStorefrontUpdates: (storefronts) => {
    return dispatch(actions.apps.fetchStorefrontUpdates(storefronts));
  },

  getInstalledApps: (storefront) => {
    return dispatch(actions.storefronts.getInstalledApps(storefront));
  },
});

export class Storefronts extends React.Component {
  static contextTypes = {
    client: pt.object.isRequired,
    openModal: pt.func.isRequired,
    notifySuccess: pt.func.isRequired,
    notifyError: pt.func.isRequired,
    notifyDeleted: pt.func.isRequired,
  };

  static propTypes = {
    params: pt.object,
    router: pt.object,
    record: pt.object,
    storefronts: pt.object,

    fetchAllStorefronts: pt.func,
    fetchAllDeprecatedApps: pt.func,
    fetchStorefront: pt.func,
    updateStorefront: pt.func,
    createStorefront: pt.func,
    deleteStorefront: pt.func,
    updateApp: pt.func,
    fetchUpgradeConflicts: pt.func,
    autoUpdateInstalledThemes: pt.func,
    fetchStorefrontUpdates: pt.func,
    getInstalledApps: pt.func,
  };

  mounted = false;

  constructor(props) {
    super(props);
    this.state = {
      loaded: false,
      cloneId: null,
      onClickPublish: this.onClickPublish.bind(this),
      onClickRename: this.onClickRename.bind(this),
      onClickDuplicate: this.onClickDuplicate.bind(this),
      onClickDelete: this.onClickDelete.bind(this),
      onUploadThemeSuccess: this.onUploadThemeSuccess.bind(this),
      renderThumbnail: this.renderThumbnail.bind(this),
    };
  }

  componentDidMount() {
    this.mounted = true;

    this.loadStorefronts();
  }

  componentWillUnmount() {
    this.mounted = false;
  }

  async loadStorefronts() {
    const {
      fetchAllStorefronts,
      fetchAllDeprecatedApps,
      autoUpdateInstalledThemes,
    } = this.props;

    await Promise.all([fetchAllStorefronts(), fetchAllDeprecatedApps()]);

    await autoUpdateInstalledThemes();
    await this.fetchAndAutoUpdateStorefronts();

    this.setState({ loaded: true });
  }

  async fetchAndAutoUpdateStorefronts() {
    const {
      updateStorefront,
      updateApp,
      fetchAllStorefronts,
      fetchStorefrontUpdates,
    } = this.props;

    const storefrontUpdates = await fetchStorefrontUpdates();

    // Iterate through storefront apps and update all automatically
    // TODO: we need another UI for managing "major" updates
    // This approach will work in the short-term before we plan to make major updates
    if (storefrontUpdates) {
      let updated = false;

      const appUpdates = {};
      for (const [storefrontId, updates] of Object.entries(storefrontUpdates)) {
        if (updates?.[0]?.storefrontVersion) {
          const { storefrontApp, storefrontVersion } = updates[0];

          try {
            console.log('Auto updating storefront app version', { updates });

            await updateStorefront(storefrontId, {
              app_version: storefrontVersion.version,
            });

            updated = true;
            appUpdates[storefrontApp.app_id] = storefrontVersion.version;
          } catch (error) {
            console.error(error);
          }
        }
      }

      if (updated) {
        await fetchAllStorefronts();

        // Update installed apps
        for (const [id, version] of Object.entries(appUpdates)) {
          await updateApp(id, { version });
        }
      }
    }
  }

  renderThumbnail(storefront, type = 'desktop') {
    const { name, thumbnails } = storefront;
    const thumbnail = thumbnails?.[type];
    const loading = thumbnails ? thumbnails.loading : true;

    return (
      <div
        className={classNames('storefront-thumbnail', {
          loading,
        })}
      >
        {loading ? (
          <div className="loading-preload">
            <Loading />
          </div>
        ) : thumbnail ? (
          <img src={thumbnail} alt={name} />
        ) : (
          <span className="storefront-thumbnail-icon">
            <Icon fa="laptop-code" faType="light" />
          </span>
        )}
      </div>
    );
  }

  getTargetStorefront(event) {
    const { params, record, storefronts } = this.props;

    if (params.id) {
      return record;
    }

    // Note this is used in both the list and view containers
    const id = event?.currentTarget?.dataset.id || params.id;
    if (id) {
      return storefronts.results.find((storefront) => storefront.id === id);
    }
  }

  onUploadThemeSuccess = async (app) => {
    this.context.notifySuccess(`Uploaded ${app.name}`);

    await this.loadStorefronts();
  };

  onClickPublish = (event) => {
    event?.preventDefault();
    const { params, fetchStorefront, updateStorefront } = this.props;

    const storefront = this.getTargetStorefront(event);
    if (!storefront) {
      return;
    }

    this.context.openModal('Confirm', {
      title: `Publish ${storefront.name}`,
      action: 'Publish',
      message: (
        <>
          <p>
            This is what your customers see when they visit your online store.
          </p>
          <div className="storefront-primary storefront-publish-preview">
            <div className="storefront-thumbnail-container">
              <div className="storefront-thumbnail-primary-desktop">
                {this.renderThumbnail(storefront, 'desktop')}
              </div>
              <div className="storefront-thumbnail-primary-mobile">
                {this.renderThumbnail(storefront, 'mobile')}
              </div>
            </div>
          </div>
        </>
      ),
      loadingTitle: `Publishing ${storefront.name}, please wait...`,
      onConfirm: async () => {
        await updateStorefront(storefront.id, {
          $publish: true,
          primary: true,
        });

        this.context.notifySuccess(`Published ${storefront.name}`);

        if (params.id) {
          await fetchStorefront(params.id);
        } else {
          await this.loadStorefronts();
        }
      },
    });
  };

  onClickRename = (event) => {
    event?.preventDefault();
    const { params, fetchStorefront, updateStorefront } = this.props;

    const storefront = this.getTargetStorefront(event);
    if (!storefront) {
      return;
    }

    this.context.openModal('ConfirmRename', {
      title: storefront.name,
      nameInput: {
        label: 'Storefront name',
        defaultValue: storefront.name,
        placeholder: storefront.name,
        hint: `Changing your storefront name will not affect domains or other settings.`,
      },
      onConfirm: async (name) => {
        if (name) {
          await updateStorefront(storefront.id, { name });

          this.context.notifySuccess(`Renamed ${storefront.name} to ${name}`);

          if (params.id) {
            await fetchStorefront(params.id);
          } else {
            await this.loadStorefronts();
          }
        }
      },
    });
  };

  onClickDuplicate = (event) => {
    event?.preventDefault();
    const { params, router, createStorefront } = this.props;

    const storefront = this.getTargetStorefront(event);
    if (!storefront) {
      return;
    }

    this.context.openModal('ConfirmRename', {
      titleMessage: `Duplicate ${storefront.name}`,
      message: (
        <p>
          A copy of your storefront will be created including all settings and
          configurations.
        </p>
      ),
      nameInput: {
        title: 'Storefront name',
        placeholder: storefront.name,
      },
      action: 'Duplicate',
      loadingTitle: `Duplicating ${storefront.name}`,
      onConfirm: async (name) => {
        const created = await createStorefront({
          cloned_id: storefront.id,
          name,
        });

        this.context.notifySuccess(`Duplicated ${storefront.name}`);

        if (params.id) {
          router.replace(`/storefronts/${created.id}`);
        } else {
          await this.loadStorefronts();
        }
      },
    });
  };

  onClickDelete = (event) => {
    event?.preventDefault();
    const { params, router, deleteStorefront } = this.props;

    const storefront = this.getTargetStorefront(event);
    if (!storefront) {
      return;
    }

    const confirmName =
      storefront.name?.replace(/\([^(]+\)/, '').trim() || storefront.id;

    this.context.openModal('ConfirmDelete', {
      title: storefront.name,
      action: 'Confirm',
      confirmInput: {
        description: 'Enter the storefront name to confirm',
        value: confirmName,
      },
      message: 'Are you sure you want to delete this storefront permanently?',
      loadingTitle: `Deleting ${storefront.name}`,
      onConfirm: async () => {
        await deleteStorefront(storefront.id);

        this.context.notifyDeleted(storefront.name);

        if (params.id) {
          router.push('/storefronts');
        } else {
          await this.loadStorefronts();
        }
      },
    });
  };

  render() {
    if (!this.state.loaded) {
      return <ViewLoading />;
    }

    return <StorefrontsPage {...this.props} {...this.state} />;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Storefronts);
